package uz.mint.frontend.api

import kotlinx.browser.window
import kotlinx.coroutines.await
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.i18n.Locale

/**
 * Client for the backend's public catalog endpoints.
 *
 * The calls happen in the browser, not at build time: the landing page is statically exported
 * for SEO, and prices change without a redeploy. Crawlers therefore index the hero and
 * advantages copy — which is the part the ТЗ cares about — while the plan grid fills in on
 * load. The endpoints are anonymous by design, so there is no token to attach.
 *
 * **Reads are paged.** The catalogue runs to thousands of plans; the landing page shows six and
 * the full listing shows a screen at a time. Nothing here ever asks for all of them.
 *
 * No kotlinx.serialization here on purpose: the payloads are flat shapes, and pulling in the
 * plugin plus runtime to parse a dozen fields is more build surface than it earns. The `*Json`
 * interfaces are the raw wire shapes and never escape this file — the `to…` functions validate
 * them at the boundary so a malformed payload fails there rather than rendering `undefined`.
 */
object CatalogApi {

    /** Landing preview size: two rows of three on a wide viewport. */
    const val PREVIEW_PAGE_SIZE = 6

    /** Full-listing page size, and the backend's own default. */
    const val DEFAULT_PAGE_SIZE = 12

    /**
     * One page of plans.
     *
     * @param locale which language to ask for plan names in. The backend resolves the
     *   translation and its fallback, so [Plan.name] arrives ready to render.
     * @param category restrict to one storefront tab, or null for every category.
     * @param query free-text destination search; blank means no filter.
     * @param destination narrow to one country or one multi-country bundle — the second step of
     *   the country-first flow, once the customer has said where they are going.
     */
    suspend fun fetchPlans(
        locale: Locale,
        category: PlanCategory? = null,
        page: Int = 0,
        size: Int = DEFAULT_PAGE_SIZE,
        query: String = "",
        destination: Destination? = null,
    ): PlanPage {
        val params = buildList {
            add("lang=${locale.code}")
            add("page=$page")
            add("size=$size")
            category?.let { add("category=${it.name.lowercase()}") }
            destination?.queryParam?.let { (name, value) -> add("$name=${encodeURIComponent(value)}") }
            query.trim().takeIf { it.isNotEmpty() }?.let { add("q=${encodeURIComponent(it)}") }
        }

        val raw = parse<PlanPageJson>(get("/api/catalog/plans?" + params.joinToString("&")))
        val items = raw.items ?: throw CatalogException("Catalog page is missing 'items'")

        return PlanPage(
            items = items.map { it.toPlan() },
            page = raw.page ?: 0,
            size = raw.size ?: size,
            total = raw.total ?: 0,
            totalPages = raw.totalPages ?: 0,
        )
    }

    /**
     * One plan, fetched when the checkout dialog opens.
     *
     * Re-read here rather than carried over from the card: the grid may have been rendered
     * minutes ago, or baked into a static export hours ago, and the figure beside a pay button
     * has to be the one that will actually be charged.
     *
     * No payment link comes back — the cashier link names an *order*, so it is minted by
     * `AccountApi.createOrder` once the customer is signed in and has committed.
     */
    suspend fun fetchPlanDetail(locale: Locale, packageCode: String): PlanDetail {
        val path = "/api/catalog/plans/${encodeURIComponent(packageCode)}?lang=${locale.code}"
        val raw = parse<PlanDetailJson>(get(path))
        val plan = raw.plan ?: throw CatalogException("Plan detail is missing 'plan'")

        return PlanDetail(plan = plan.toPlan())
    }

    private suspend fun get(path: String): String {
        val response = window.fetch("${SiteConfig.apiOrigin}$path").await()
        if (!response.ok) {
            throw CatalogException("Catalog request failed with HTTP ${response.status}")
        }
        return response.text().await()
    }

    private fun <T> parse(body: String): T = try {
        JSON.parse<T>(body)
    } catch (e: Throwable) {
        throw CatalogException("Catalog response was not valid JSON: ${e.message}")
    }
}

class CatalogException(message: String) : RuntimeException(message)

/**
 * A sellable plan as the storefront sees it. Mirrors the backend's `PlanDto`, which
 * deliberately omits provider cost and markup — margin is not public.
 */
data class Plan(
    val packageCode: String,
    val name: String,
    val countryCode: String?,
    val region: String?,
    val dataAmountMb: Int,
    val validityDays: Int,
    /**
     * Retail price in the catalogue's own currency (USD). Kept for parity with the DTO — the
     * storefront never shows it, see [uz.mint.frontend.api.priceLabel].
     *
     * **Optional, and not merely for tidiness.** Since markup comes from the cost ladder alone, a
     * plan no band covers has no price at all, and the backend sends null. Requiring it here would
     * throw at the boundary and take down the whole grid over one unpriced card — the card itself
     * already knows how to say «Цена по запросу», which is what a null `priceUzs` renders as.
     */
    val price: Double?,
    val currency: String,
    /**
     * The price customers actually see, converted server-side from [price]. Null when no FX rate
     * is configured, and then the card says so — the storefront no longer falls back to USD.
     */
    val priceUzs: Double?,
)

/** One window onto the catalogue, plus enough to render page controls. */
data class PlanPage(
    val items: List<Plan>,
    val page: Int,
    val size: Int,
    val total: Int,
    val totalPages: Int,
) {
    val hasPrevious: Boolean get() = page > 0
    val hasNext: Boolean get() = page + 1 < totalPages
}

/** One plan, freshly read at the moment of checkout. */
data class PlanDetail(val plan: Plan)

private external interface PlanPageJson {
    val items: Array<PlanJson>?
    val page: Int?
    val size: Int?
    val total: Int?
    val totalPages: Int?
}

private external interface PlanDetailJson {
    val plan: PlanJson?
}

private external interface PlanJson {
    val packageCode: String?
    val name: String?
    val countryCode: String?
    val region: String?
    val dataAmountMb: Int?
    val validityDays: Int?
    val price: Double?
    val currency: String?
    val priceUzs: Double?
}

private fun PlanJson.toPlan(): Plan {
    fun <T : Any> require(value: T?, field: String): T =
        value ?: throw CatalogException("Catalog entry is missing '$field'")

    return Plan(
        packageCode = require(packageCode, "packageCode"),
        name = require(name, "name"),
        countryCode = countryCode,
        region = region,
        dataAmountMb = require(dataAmountMb, "dataAmountMb"),
        validityDays = require(validityDays, "validityDays"),
        price = price,
        currency = currency ?: "USD",
        priceUzs = priceUzs,
    )
}
